import { ChevronDownIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import {
  NavLink,
  useMatches,
  useNavigate,
  useOutletContext
} from "react-router-dom";
import type { Tab } from "../interface";
import ScrollButton from "./ScrollButton";

interface IProps {
  tabs: Array<Tab>;
  baseURL: string;
  styles?: {
    container?: string;
    tab?: string;
  };
  hasScroll?: boolean;
}

function isInViewport(element: Element | null) {
  const rect = element?.getBoundingClientRect();

  return rect
    ? rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
    : false;
}

function TabsOne({
  tabs = [],
  baseURL = "",
  styles = {},
  hasScroll = false
}: IProps) {
  const navbarRef = useRef<HTMLDivElement>(null);
  const matches = useMatches();
  const [scrollPosition, setScrollPosition] = useState(0);
  const outletContxt = useOutletContext() as {
    whiteLabelStyle?: { theme?: { color?: { border?: string } } };
  };
  const { whiteLabelStyle } = outletContxt ?? {};

  const tabLinks = navbarRef.current?.querySelectorAll("a");
  const isLastElementVisible = isInViewport(
    tabLinks?.[tabLinks?.length - 1] || null
  );
  const shouldShowScrollRightIndicator = !isLastElementVisible;
  const shouldShowScrollLeftIndicator = scrollPosition > 0;
  const scrollOffset = 150;

  // Mobile-specific state and logic
  const mobileNavbarRef = useRef<HTMLDivElement>(null);
  const [mobileScrollPosition, setMobileScrollPosition] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const mobileTabLinks = mobileNavbarRef.current?.querySelectorAll("a");
  const isLastMobileElementVisible = isInViewport(
    mobileTabLinks?.[mobileTabLinks?.length - 1] || null
  );
  const shouldShowMobileScrollRightIndicator = !isLastMobileElementVisible;
  const shouldShowMobileScrollLeftIndicator = mobileScrollPosition > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Function to scroll to a specific tab and navigate
  const handleTabSelect = (tabHref: string) => {
    navigate(tabHref);
    setIsDropdownOpen(false);

    // Scroll to the tab after navigation
    setTimeout(() => {
      const tabIndex = tabs.findIndex(
        (tab) => `${baseURL}${tab.href}` === tabHref
      );
      const tabElement = mobileTabLinks?.[tabIndex];
      if (tabElement && mobileNavbarRef.current) {
        tabElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
      }
    }, 100);
  };

  return (
    <div>
      {/* Mobile horizontal scrollable tabs */}
      <div className="md:!hidden relative flex items-center bg-gray-50 border-b border-gray-200">
        {/* Scrollable tabs container - confined width */}
        <div className="flex-1 relative overflow-hidden">
          <nav
            ref={mobileNavbarRef}
            className="flex overflow-x-auto scrollbar-hide py-1"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            aria-label="Mobile tabs"
          >
            <ScrollButton
              scrollOffset={scrollOffset}
              direction="left"
              navbarRef={mobileNavbarRef}
              shouldShow={shouldShowMobileScrollLeftIndicator}
              setScrollPosition={setMobileScrollPosition}
            />

            {tabs?.map((tab) => {
              const url = `${baseURL}${tab.href}`;
              const matchedRoute = matches.find((m) => m.pathname === url);
              const isActive = matchedRoute ? true : false;

              return (
                <NavLink
                  key={tab.name}
                  to={url}
                  className={classNames(
                    isActive
                      ? "text-gray-900 bg-white border border-gray-200 rounded-t-lg shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
                    "relative min-w-max overflow-hidden py-3 px-4 text-sm font-medium text-center focus:z-10 whitespace-nowrap flex-shrink-0 transition-colors duration-150"
                  )}
                  aria-current={isActive ? "page" : undefined}
                  onClick={(e) => {
                    e.currentTarget.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "center"
                    });
                  }}
                >
                  {tab.name}
                </NavLink>
              );
            })}

            <ScrollButton
              direction="right"
              navbarRef={mobileNavbarRef}
              scrollOffset={scrollOffset}
              setScrollPosition={setMobileScrollPosition}
              shouldShow={shouldShowMobileScrollRightIndicator}
            />
          </nav>
        </div>

        {/* Separator */}
        <div className="flex items-center justify-center px-3 h-full">
          <div className="w-px h-6 bg-gray-300"></div>
        </div>

        {/* Dropdown button */}
        <div ref={dropdownRef} className="relative flex items-center pr-3">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="bg-white border border-gray-200 rounded-full p-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-150"
            aria-label="More tabs"
          >
            <ChevronDownIcon className="w-3 h-3 text-gray-500" />
          </button>

          {/* Dropdown menu */}
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] min-w-[150px] max-h-[300px] overflow-y-auto">
              {tabs.map((tab) => {
                const url = `${baseURL}${tab.href}`;
                const matchedRoute = matches.find((m) => m.pathname === url);
                const isActive = matchedRoute ? true : false;

                return (
                  <button
                    key={tab.name}
                    onClick={() => handleTabSelect(url)}
                    className={classNames(
                      "w-full px-4 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg",
                      isActive
                        ? "text-gray-900 bg-gray-100 font-medium"
                        : "text-gray-700"
                    )}
                  >
                    {tab.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Desktop tabs */}
      <div className="hidden md:block relative">
        <nav
          ref={navbarRef}
          className={classNames(
            "flex max-h-full [&>button]:hover:block",
            hasScroll ? "overflow-hidden" : "overflow-auto",
            styles.container || ""
          )}
          aria-label="Index"
        >
          <ScrollButton
            scrollOffset={scrollOffset}
            direction="left"
            navbarRef={navbarRef}
            shouldShow={hasScroll && shouldShowScrollLeftIndicator}
            setScrollPosition={setScrollPosition}
          />

          {tabs?.map((tab) => {
            const url = `${baseURL}${tab?.href}`;
            const matchedRoute = matches.find((m) => m.pathname === url);
            const isActive = matchedRoute ? true : false;

            return (
              <NavLink
                key={tab.name}
                to={url}
                className={classNames(
                  isActive
                    ? `text-gray-900 border-b-2 ${
                        whiteLabelStyle?.theme?.color?.border ||
                        "border-indigo-500"
                      } hover:text-gray-600 bg-white border-r !border-r-gray-200 `
                    : "text-gray-600 hover:text-gray-600 bg-gray-100 border-r border-gray-200",
                  "relative min-w-max overflow-hidden  py-3 px-6 text-sm font-normal text-center hover:bg-gray-50 focus:z-10 rounded-none  ",
                  styles.tab || ""
                )}
                aria-current={isActive ? "page" : undefined}
                onClick={(e) => {
                  e.currentTarget.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest"
                  });
                }}
              >
                <span
                  className={classNames(
                    "text-sm",
                    isActive ? "font-medium" : ""
                  )}
                >
                  {tab.name}
                </span>
              </NavLink>
            );
          })}

          <ScrollButton
            direction="right"
            navbarRef={navbarRef}
            scrollOffset={scrollOffset}
            setScrollPosition={setScrollPosition}
            shouldShow={hasScroll && shouldShowScrollRightIndicator}
          />
        </nav>
      </div>
    </div>
  );
}

export default React.memo(TabsOne);
